import React from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";

type PropertyInfographicProps = {
  property?: any;
};

const getCurrency = (property?: any): string => {
  const isUganda =
    !!property?.location?.region ||
    property?.location?.country === "UG";
  return isUganda ? "UGX" : "RWF";
};

const currency = (n: number, currencyCode: string = "RWF") =>
  `${currencyCode} ${Number(n || 0).toLocaleString()}`;

const breakdownColors = ["#60A5FA", "#34D399"];

export default function PropertyInfographic({
  property,
}: PropertyInfographicProps) {
  if (!property) {
    return (
      <div className="py-6 bg-gray-50 min-h-screen text-slate-800">
        <div className="mx-auto">
          <div className="md:col-span-2 bg-white rounded-2xl shadow p-6 flex flex-col">
            <div className="text-center py-8">
              <div className="text-slate-500">No property data available</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const valuation = property.valuationDetails || {};
  const currencyCode = getCurrency(property);

  // Extract data for pie chart
  const pieData = [
    {
      name: "Improvements (Depreciated)",
      value: valuation.improvementsValue || 0,
    },
    {
      name: "Land Value",
      value: valuation.landValue || 0,
    },
  ];

  // Calculate price per m²
  const landArea = property.landArea || property.landParcel?.area || 0;
  const landValue = valuation.landValue || 0;
  const pricePerM2 = landArea > 0 ? Math.round(landValue / landArea) : 0;

// Prepare component breakdown from property data
  const componentBreakdownData = [
    {
      name: "Main House",
      value: (valuation.mainHouseArea || 0) * (valuation.mainHouseRate || 0),
    },
    {
      name: "Annex House",
      value: (valuation.annexHouseArea || 0) * (valuation.annexRate || 0),
    },
    {
      name: "Fence",
      value: (valuation.fenceLength || 0) * (valuation.fenceRate || 0),
    },
    {
      name: "Retaining Walls",
      value: (valuation.retainingWallsLength || 0) * (valuation.retainingWallRate || 0),
    },
    {
      name: "Garden",
      value: (valuation.gardenArea || 0) * (valuation.gardenRate || 0),
    },
    {
      name: "Paved Area",
      value: (valuation.pavedArea || 0) * (valuation.pavedAreaRate || 0),
    },
    {
      name: "Entrance Gate",
      value: (valuation.entranceGateLength || 0) * (valuation.entranceGateRate || 0),
    },
    {
      name: "Septic Tanks",
      value: (valuation.septicTankValue || 0) * (valuation.septicTankNumber || 0),
    },
  ].filter((item) => item.value > 0);

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return "Jan 31, 2022";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Sample trend data (since we don't have historical data in the property object)
  const trendSample = [{ month: "Jan-17", value: pricePerM2 }];

  return (
    <div className="py-6 bg-gray-50 min-h-screen text-slate-800">
      <div className="mx-auto">
        <div className="md:col-span-2 bg-white rounded-2xl shadow p-6 flex flex-col">
          <div className="flex items-start justify-between">
            <div className="text-right">
              <div className="text-lg text-slate-500">Date of Report</div>
              <div className="text-lg font-medium">
                {formatDate(valuation.valuationDate)}
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="col-span-2 bg-white p-4 rounded-xl border">
              <h3 className="text-sm font-medium text-slate-700">
                Market Value Composition
              </h3>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={4}
                      isAnimationActive={false}
                    >
                      {pieData.map((entry, idx) => (
                        <Cell
                          key={`cell-${idx}`}
                          fill={breakdownColors[idx % breakdownColors.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => currency(value, currencyCode)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-3 flex gap-3">
                <div className="flex-1">
                  <div className="text-xs text-slate-500">
                    Improvements (Depreciated)
                  </div>
                  <div className="font-semibold">
                    {currency(valuation.improvementsValue || 0, currencyCode)}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="text-xs text-slate-500">Land</div>
                  <div className="font-semibold">
                    {currency(valuation.landValue || 0, currencyCode)}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border">
              <h3 className="text-sm font-medium text-slate-700">
                Price per m²
              </h3>
              <div className="mt-4 text-2xl font-semibold">
                {currency(pricePerM2, currencyCode)} / m²
              </div>
              <div className="text-xs text-slate-500 mt-2">
                Based on reported land value and plot area ({landArea} m²)
              </div>

              <div className="mt-4">
                <h4 className="text-xs text-slate-500">Trend</h4>
                <div style={{ width: "100%", height: 90 }}>
                  <ResponsiveContainer>
                    <LineChart data={trendSample}>
                      <XAxis dataKey="month" hide />
                      <YAxis hide domain={["dataMin", "dataMax"]} />
                     <Tooltip formatter={(v: number) => currency(v, currencyCode)} />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#60A5FA"
                        dot={false}
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-white p-4 rounded-xl border">
            <h3 className="text-sm font-medium text-slate-700">
              Component Breakdown (selectable)
            </h3>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={componentBreakdownData}
                  layout="vertical"
                  margin={{ top: 20, right: 40, left: 120, bottom: 20 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={true}
                    vertical={false}
                    stroke="#e5e7eb"
                  />
                  <XAxis
                    type="number"
                    tickFormatter={(value) => currency(value, currencyCode)}
                    fontSize={11}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#6b7280" }}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    fontSize={11}
                    width={120}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#374151" }}
                    interval={0} // This ensures ALL labels are shown
                  />
                  <Tooltip
                    formatter={(value: number) => currency(value, currencyCode)}
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      fontSize: "12px",
                    }}
                  />
                  <Bar
                    dataKey="value"
                    name="Value"
                    barSize={14}
                    fill="#60A5FA"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
